import {
    ResourceMenuViewModel,
    SettingsViewModel,
    SiteChromeViewModel,
    SiteFooterViewModel
} from '@/types/contentServerContract';
import {
    CountryOptionsViewModel,
    Feature,
    ProductGuideConfigurationViewModel,
    SiteName
} from '@/types/apiServerContract';
import { isExternalProductGuide } from '@/core/environmentService';

declare global {
    interface Window {
        servercontext: {
            siteChrome: SiteChromeViewModel;
            culture: string;
            customCulture: string;
            erpBusinessEntityId: string;
            marketId: string;
            businessEntityName: string;
            siteId: number;
            languageCode: string;
            countryCode: string;
            contentHost: string;
            productMediaUrl: string;
            url: string;
            apiHost: string;
            siteAssetsUrl: string;
            googleTagManagerId: string;
            preview: boolean;
            isExternalProductGuide: boolean;
            externalProductGuideLink: string;
            externalProductGuideLinkText: string;
            externalProductGuideSettings?: Partial<ExternalProductGuideConfigurationViewModel>;
            externalProductGuideId?: string;
            dictionary: {[key: string]: string};
            theming: {
                themeString: string;
            };
            featureFlags: { name: Feature, active: boolean }[];
            siteName: SiteName;
            maintenance: boolean;
            punchOutJwtToken: string | undefined;
        },
        externalContext?: {
            campaignSource?: string;
        },
        Cookiebot?: {
            // Source: https://www.cookiebot.com/en/developer/
            consent?: {
                necessary: boolean,     // True if current user has accepted necessary cookies. The property is read only.
                preferences: boolean,   // True if current user has accepted preference cookies. The property is read only.
                statistics: boolean,    // True if current user has accepted statistics cookies. The property is read only.
                marketing: boolean,     // True if current user has accepted marketing cookies. The property is read only.
            },
            method:	string | null,      // The way the consent was given. Either ‘implied’ when a consent is given automatically or ‘explicit’ when given by the user. If there is no consent the value is null. If the consent method is unknown (old consents) it is considered ‘implied’. The property is read only.
            consented: boolean,         // True if the user has accepted cookies. The property is read only.
            declined: boolean,          // True if the user has declined the use of cookies. The property is read only.
            hasResponse: boolean,       // True if the user has responded to the dialog with either ‘accept’ or ‘decline’. The property is read only.
            doNotTrack:	boolean,        // True if the user has enabled the web browser’s ‘Do not track’ (DNT) setting. If DNT is enabled Cookiebot will not set the third party cookie CookieConsentBulkTicket used for bulk consent. The property is read only.
            regulations: {
                gdprApplies: boolean,   // Allows you to determine if GDPR applies based on the user’s geo-location. The property is read only.
                ccpaApplies: boolean,   // Allows you to determine if CCPA applies based on the user’s geo-location. The property is read only.
                lgpdApplies: boolean,   // Allows you to determine if LGPD applies based on the user’s geo-location. The property is read only.
            }
        }
    }
}
export interface ExternalProductGuideConfigurationViewModel extends ProductGuideConfigurationViewModel {
    externalId?: string;
}
export interface InitialSiteInfo {
    erpBusinessEntityId: number | null;
    marketId: number | null;
    siteId: number;
    countryCode: string | null;
    url: string | null;
    languageCode: string | null;
    culture: string | null;
    customCulture: string | null;
}
export interface SiteInfo extends InitialSiteInfo {
    countryOptions: null | CountryOptionsViewModel;
}
const onErrorImage = new URL('../assets/icons/fallback.svg', import.meta.url).href;
const onErrorImageOprema = new URL('../assets/icons/oprema-fallback.svg', import.meta.url).href;

class ServerContextService {
    constructor() {
        if (!window.servercontext) {
            throw new Error('### window.servercontext object should exist ###');
        }
        if (!window.servercontext.siteChrome) {
            throw new Error('### window.servercontext.siteChrome object should exist ###');
        }
    }

    public get footer(): SiteFooterViewModel {
        return window.servercontext.siteChrome.siteFooter;
    }

    public get settings(): SettingsViewModel {
        return window.servercontext.siteChrome.settings;
    }

    public get resourceMenu(): ResourceMenuViewModel | null {
        return window.servercontext.siteChrome.resourceMenu || null;
    }

    public get siteInfo(): InitialSiteInfo {
        const erpBusinessEntityId = parseInt(window.servercontext.erpBusinessEntityId, 10);
        const marketId = parseInt(window.servercontext.marketId, 10);

        return ({
            culture: window.servercontext.culture,
            customCulture: window.servercontext.customCulture,
            url: window.servercontext.url,
            countryCode: window.servercontext.countryCode,
            languageCode: window.servercontext.languageCode,
            siteId: window.servercontext.siteId,
            erpBusinessEntityId: Number.isNaN(erpBusinessEntityId) ? null : erpBusinessEntityId,
            marketId: Number.isNaN(marketId) ? null : marketId
        });
    }

    public get apiHost(): string {
        return window.servercontext.apiHost;
    }

    public get contentHost(): string {
        return window.servercontext.contentHost;
    }

    public get productMediaUrl(): string {
        return window.servercontext.productMediaUrl;
    }

    public get isPreview(): boolean {
        return window.servercontext.preview;
    }

    public get isExternalProductGuide(): boolean {
        return isExternalProductGuide();
    }

    public get themeString(): string {
        return window.servercontext.theming.themeString;
    }

    public get isOprema(): boolean {
        return window.servercontext.siteName === SiteName.Oprema;
    }

    public get siteName(): SiteName {
        return window.servercontext.siteName;
    }

    public productUrl(product: { itemId: string, url?: string, mpn?: string }) {
        const externalProductLink = window.servercontext.externalProductGuideSettings?.externalProductLink;
        return encodeURI(this.isExternalProductGuide && externalProductLink
            ? externalProductLink.replace(/XXX/gi, (product.mpn || product.itemId))
            : product.url ? product.url : '');
    }

    public get externalProductLinkTarget() {
        return this.isExternalProductGuide ? '_blank' : null;
    }

    public get externalProductLinkText() {
        return window.servercontext.externalProductGuideSettings?.externalProductLinkText;
    }

    public set externalProductGuideSettings(settings: Partial<ExternalProductGuideConfigurationViewModel> | undefined) {
        window.servercontext.externalProductGuideSettings = settings;
    }

    public get externalProductGuideSettings(): Partial<ExternalProductGuideConfigurationViewModel> | undefined {
        return window.servercontext.externalProductGuideSettings;
    }

    public get punchOutJwtToken(): string | undefined {
        return window.servercontext.punchOutJwtToken;
    }

    public get imageNotFound() {
        return this.isOprema ? onErrorImageOprema : onErrorImage;
    }

    public isFeatureEnabled(featureName: Feature): boolean {
        const feature = window.servercontext.featureFlags.find(feature => feature.name === featureName);
        return feature?.active ?? false;
    }

    public isAddressSuggestFeatureEnabled():boolean {
        return this.isFeatureEnabled(Feature.AddressSuggest);
    }
}

export default new ServerContextService();
